
import React from "react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Shield, ArrowLeft } from "lucide-react";

export default function PrivacyPolicy() {
  return (
    <div className="container mx-auto px-4 py-12 max-w-4xl" id="privacy-top">
      <div className="mb-8">
        <Button variant="ghost" asChild className="mb-4">
          <Link to="/" className="flex items-center">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Home
          </Link>
        </Button>
        
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <Shield className="h-6 w-6" />
          Privacy Policy
        </h1>
        <p className="text-muted-foreground mt-2">Last updated: April 17, 2025</p>
      </div>
      
      <div className="prose dark:prose-invert max-w-none">
        <p>
          Ogmai ("Company," "we," "us," or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your personal information when you visit our website, use our platform, or interact with any of our services, including any mobile or desktop applications (collectively, the "Services").
        </p>
        
        <p>
          By accessing or using the Services, you agree to the terms of this Privacy Policy.
        </p>
        
        <hr className="my-8" />
        
        <section className="mb-8">
          <h2 className="text-2xl font-bold">1. INFORMATION WE COLLECT</h2>
          
          <h3 className="text-xl font-semibold mt-6">1.1 Information You Provide Directly</h3>
          <p>
            We collect personal information that you voluntarily provide to us, including:
          </p>
          <ul>
            <li><strong>Account Data:</strong> Name, email, phone number, company, job title, and account credentials.</li>
            <li><strong>Transaction Information:</strong> Details related to payments, subscriptions, and services purchased.</li>
            <li><strong>Communications:</strong> Messages, inquiries, feedback, and customer support interactions.</li>
            <li><strong>Marketing Preferences:</strong> Your choices regarding newsletters, updates, and event invitations.</li>
          </ul>
          
          <h3 className="text-xl font-semibold mt-6">1.2 Information from Third Parties</h3>
          <p>
            We may receive personal information about you from third-party sources, such as:
          </p>
          <ul>
            <li>Social media platforms (e.g., LinkedIn, Twitter)</li>
            <li>Payment processors (e.g., Stripe, Mercado Pago)</li>
            <li>Analytics and marketing partners</li>
          </ul>
          
          <h3 className="text-xl font-semibold mt-6">1.3 Automatically Collected Information</h3>
          <p>
            When you interact with our Services, we automatically collect data such as:
          </p>
          <ul>
            <li><strong>Device Information:</strong> IP address, device type, operating system, browser type, screen resolution.</li>
            <li><strong>Usage Data:</strong> Pages visited, time on page, links clicked, referring/exit URLs.</li>
            <li><strong>Cookies & Similar Technologies:</strong> Including session cookies, persistent cookies, pixels, and local storage.</li>
          </ul>
          
          <p>
            You can control cookies via browser settings. For more information, visit <a href="https://www.allaboutcookies.org/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">https://www.allaboutcookies.org/</a>.
          </p>
        </section>
        
        <hr className="my-8" />
        
        <section className="mb-8">
          <h2 className="text-2xl font-bold">2. HOW WE USE YOUR INFORMATION</h2>
          <p>
            We use your personal information for the following purposes:
          </p>
          <ul>
            <li>To provide and operate our Services</li>
            <li>To personalize and improve your experience</li>
            <li>To process transactions and manage accounts</li>
            <li>To send transactional emails and respond to inquiries</li>
            <li>To market products and services (with consent)</li>
            <li>To detect and prevent fraud, abuse, and violations</li>
            <li>To comply with legal obligations and enforce terms</li>
          </ul>
          
          <p>
            We may use aggregated or anonymized data for analytics, research, or benchmarking purposes.
          </p>
        </section>
        
        <hr className="my-8" />
        
        <section className="mb-8">
          <h2 className="text-2xl font-bold">3. SHARING YOUR INFORMATION</h2>
          <p>
            We do not sell your personal information. We may share your data in the following cases:
          </p>
          <ul>
            <li><strong>Service Providers:</strong> Hosting, support, payment processing, marketing, analytics, and cloud services.</li>
            <li><strong>Business Transfers:</strong> As part of a merger, sale, acquisition, or similar corporate transaction.</li>
            <li><strong>Legal Requirements:</strong> In response to subpoenas, court orders, or legal processes.</li>
            <li><strong>Protection of Rights:</strong> To protect the rights, safety, and property of Ogmai, its users, or the public.</li>
          </ul>
        </section>
        
        <hr className="my-8" />
        
        <section className="mb-8">
          <h2 className="text-2xl font-bold">4. INTERNATIONAL DATA TRANSFERS</h2>
          <p>
            Ogmai operates globally. Your information may be transferred to, and maintained on, servers located outside your country. We implement safeguards such as Standard Contractual Clauses to protect your information.
          </p>
        </section>
        
        <hr className="my-8" />
        
        <section className="mb-8">
          <h2 className="text-2xl font-bold">5. DATA RETENTION</h2>
          <p>
            We retain personal information as long as necessary to fulfill the purposes described in this Policy unless a longer retention period is required by law.
          </p>
          <p>
            You can request deletion of your data by contacting <a href="mailto:privacy@ogmai.com" className="text-blue-600 hover:underline">privacy@ogmai.com</a>.
          </p>
        </section>
        
        <hr className="my-8" />
        
        <section className="mb-8">
          <h2 className="text-2xl font-bold">6. YOUR RIGHTS AND CHOICES</h2>
          <p>
            Depending on your location, you may have rights under data protection laws to:
          </p>
          <ul>
            <li>Access or request a copy of your data</li>
            <li>Correct inaccurate or incomplete data</li>
            <li>Request deletion of your data</li>
            <li>Object to or restrict data processing</li>
            <li>Withdraw consent where applicable</li>
            <li>Opt out of marketing communications</li>
          </ul>
          
          <p>
            You can exercise your rights by contacting us at <a href="mailto:privacy@ogmai.com" className="text-blue-600 hover:underline">privacy@ogmai.com</a>.
          </p>
        </section>
        
        <hr className="my-8" />
        
        <section className="mb-8">
          <h2 className="text-2xl font-bold">7. DATA SECURITY</h2>
          <p>
            We use administrative, technical, and physical safeguards to protect your data, including:
          </p>
          <ul>
            <li>HTTPS encryption</li>
            <li>Secure authentication and access controls</li>
            <li>Regular security audits and monitoring</li>
          </ul>
          
          <p>
            However, no system is entirely secure. You use the Services at your own risk.
          </p>
        </section>
        
        <hr className="my-8" />
        
        <section className="mb-8">
          <h2 className="text-2xl font-bold">8. CHILDREN'S PRIVACY</h2>
          <p>
            Ogmai does not knowingly collect data from children under 13. If we learn we have collected personal information from a child without parental consent, we will delete it immediately.
          </p>
        </section>
        
        <hr className="my-8" />
        
        <section className="mb-8">
          <h2 className="text-2xl font-bold">9. CHANGES TO THIS POLICY</h2>
          <p>
            We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the updated Policy on our website with a new effective date.
          </p>
        </section>
        
        <hr className="my-8" />
        
        <section className="mb-8">
          <h2 className="text-2xl font-bold">10. CONTACT US</h2>
          <p>
            For questions or concerns about this Privacy Policy, contact:
            <br />
            <a href="mailto:privacy@ogmai.com" className="text-blue-600 hover:underline">privacy@ogmai.com</a>
          </p>
        </section>
      </div>
    </div>
  );
}
